import { useState, useCallback } from "react";
import classNames from "classnames";
import { Spinner, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import styles from "./LinkImage.module.css";

type Props = {
  url: string;
  variant: "thumbnail" | "modal";
};

const ERROR_MSG =
  "The link may be broken, or the image may not be publicly accessible. Try refreshing the page or updating the link.";

export function LinkImage({ url, variant }: Props) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <div
        className={
          variant === "thumbnail"
            ? styles.thumbnailErrorContainer
            : styles.modalErrorContainer
        }
      >
        {variant === "thumbnail" ? (
          <Text type="SmallPrint1" color="TextWeaker">
            Image failed to load
          </Text>
        ) : (
          <div className={styles.modalErrorMessage}>
            <Icon name="alertCircleFill" color="StrokeStronger" size={32} />
            <Text type="ParagraphHeader1" color="TextDefault">
              Image failed to load
            </Text>
            <Text type="Body1" color="TextStronger">
              {ERROR_MSG}
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        variant === "thumbnail" ? styles.thumbnailWrapper : styles.modalWrapper
      }
    >
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      )}
      <img
        src={url}
        alt="Embedded content"
        loading="lazy"
        className={classNames({
          [styles.thumbnailImage]: variant === "thumbnail",
          [styles.modalImage]: variant === "modal",
        })}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
}
